/**
 * preserveOrder.js -- pure helper for re-applying a prior row order to
 * a freshly-fetched list.
 *
 * Use case: when a list view reloads data after a row-level mutation
 * (e.g., toggling Open/Closed on the Stores list), the server's sort
 * order may move the just-mutated row to a new position (the
 * modified_at default sort puts it at position 0). That visual shift
 * confuses users who expected only the toggled row's state to change.
 *
 * This helper takes the previous list and the fresh list and returns
 * a NEW array of fresh-data items in the prior order, with any
 * server-side-new rows appended at the end. Rows that disappeared
 * from the fresh response (server-side deletion) drop out naturally.
 *
 * Pure, deterministic, no Vue dependency.
 *
 * @param {Array<Object>} prevList - the list as it was before the reload
 * @param {Array<Object>} freshList - the list as returned by the server
 * @param {string} [idKey="id"] - property name to use as a stable identity
 * @returns {Array<Object>} a new array of fresh items, ordered to match
 *   prevList where possible, with newly-appearing items appended
 */
export function preserveOrder(prevList, freshList, idKey = "id") {
    if (!Array.isArray(freshList)) {
        return [];
    }
    if (!Array.isArray(prevList) || prevList.length === 0) {
        return freshList.slice();
    }
    const freshById = new Map();
    for (const row of freshList) {
        if (row != null && row[idKey] != null) {
            freshById.set(row[idKey], row);
        }
    }
    const reordered = [];
    // First: every prev id, in prev order, replaced with its fresh data
    // if still present in the fresh response.
    for (const prev of prevList) {
        if (prev == null || prev[idKey] == null) continue;
        const id = prev[idKey];
        if (freshById.has(id)) {
            reordered.push(freshById.get(id));
            freshById.delete(id);
        }
    }
    // Then: any fresh items whose id wasn't in prev, appended in their
    // fresh order.
    for (const row of freshList) {
        if (row == null || row[idKey] == null) continue;
        if (freshById.has(row[idKey])) {
            reordered.push(row);
            freshById.delete(row[idKey]);
        }
    }
    return reordered;
}
